import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Button, CircularProgress, Popover, Stack, Typography } from '@mui/material';

import type { OpenRequirementItem } from '../store/useOpenRequirements';
import type { SubjectLanguage } from '../types';

/**
 * How the last send ended.
 *
 * `declined` is the server refusing to send and saying why — a 409, which is a
 * settled answer rather than a fault. It carries the server's own words: the
 * two cases it covers mean opposite things to staff (nothing is outstanding
 * versus something is outstanding but has no patient step yet), and only the
 * server knows which (GDN-3558).
 *
 * A union rather than an outcome plus a loose message field, so a message
 * cannot arrive without the state that explains it, or go missing from the
 * state that needs one.
 */
export type RemindOutcome =
  | { kind: 'sent' }
  | { kind: 'failed' }
  | { kind: 'declined'; message: string };

export interface RequirementAlertBadgeProps {
  items: OpenRequirementItem[];
  /** True when at least one item could not be named — drives count-only copy. */
  hasUnnamedItems: boolean;
  subject: SubjectLanguage;
  /** Absent when the provider cannot send, e.g. no contact details on file. */
  onRemind?: () => void;
  isReminding?: boolean;
  /** Result of the last send, so the popover can confirm without a toast. */
  remindOutcome?: RemindOutcome;
  onOpenPatient?: () => void;
  onOpen?: () => void;
  /**
   * `glance` is the recorder's bar: a narrow checklist to mention something
   * mid-visit. `management` is the patient page, where this replaced the
   * record section (GDN-3592) and has to carry every action that section did.
   *
   * The recorder keeps `glance` unchanged — the divergence is a wider popover
   * on one surface, not a second bar.
   */
  variant?: 'glance' | 'management';
  /**
   * The management popover's body, supplied by the caller. A node rather than
   * a flag so this component stays store-free and renderable from a story:
   * it decides how wide the popover is and what surrounds the body, the
   * container decides what the body IS.
   */
  managementContent?: ReactNode;
  /**
   * The requirement read failed (GDN-3592, Greptile P1).
   *
   * Distinct from "no requirements": the query answers a failure with an empty
   * list, so treating the two alike hid this surface exactly when something
   * had gone wrong. The pill stays, says so, and opens the manager — which
   * carries the error and its Retry.
   */
  errored?: boolean;
}

/** Hollow dot: something is still owed. Filled means settled. */
const OwedDot = ({ filled = false, color }: { filled?: boolean; color?: string } = {}) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      flexShrink: 0,
      border: `2px solid ${color ?? ELIG_TOKENS.red}`,
      backgroundColor: filled ? (color ?? ELIG_TOKENS.red) : 'transparent',
      boxSizing: 'border-box',
    }}
  />
);

/**
 * The provider alert in the recording window's patient context bar (AC-15).
 *
 * Q6 is open. This follows the standing recommendation and the reviewed round-1
 * prototype: a context-bar badge opening a compact checklist, with NO permanent
 * dismissal while items stay open — closing the popover hides the list, never
 * the badge. Financial detail stays out pending Q3.
 */
export const RequirementAlertBadge = ({
  items,
  hasUnnamedItems,
  subject,
  onRemind,
  isReminding = false,
  remindOutcome,
  onOpenPatient,
  onOpen,
  variant = 'glance',
  managementContent,
  errored = false,
}: RequirementAlertBadgeProps) => {
  const isManagement = variant === 'management';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverId = useId();

  /*
    Nothing outstanding is not an empty alert — it is no alert. That holds on
    the recorder and is why this returns null there.

    Not on the patient page (GDN-3592 criterion 7). The record section used to
    be how staff reached a settled patient's delivery history and their waived
    items; with it gone, vanishing here would leave that patient no
    requirements surface at all. The caller decides whether there is anything
    to show — it renders nothing when the patient has no requirements in any
    state — so this only has to stop swallowing the settled case.
  */
  const settled = items.length === 0 && !errored;
  if (items.length === 0 && !isManagement) return null;

  /*
    A management view, not a live-visit alert: while something is open the
    patient page pill uses the calmer amber (PM's default on GDN-3592). Alarm
    red on a page staff open deliberately would cry wolf on every visit.

    Once nothing is open it turns green (GDN-4150): amber on a patient who
    owes nothing reads as something still owed, which is the opposite of
    what is true. A failed read stays red, and `settled` already excludes
    it — the query answers a failure with an empty list, which must not
    pass for done.

    The recorder keeps red. It renders nothing once nothing is open, so green
    never arises there.
  */
  const tone = !isManagement || errored ? 'red' : settled ? 'green' : 'amber';
  const accent = ELIG_TOKENS[tone];
  const accentTint = ELIG_TOKENS[`${tone}Tint`];
  const accentSoft = ELIG_TOKENS[`${tone}Soft`];

  const open = Boolean(anchorEl);
  const [first] = items;
  // One named item gets its own words — this is what makes AC-15's "Card
  // missing" read the way it does. Anything else falls back to a count, so the
  // badge never grows unbounded or shows an identifier.
  const canNameFirst = !settled && Boolean(first?.label) && !hasUnnamedItems;
  /*
    Settled copy says what is true and offers a way in — it is a control, not
    an alert. "None open" rather than "none": the patient has requirements,
    they are just all settled, and the popover behind this lists them with
    their history.
  */
  const primaryText = errored
    ? 'Requirements — couldn’t load'
    : settled
      ? 'Requirements — none open'
      : canNameFirst
        ? `${first.label} missing`
        : `${items.length} item${items.length === 1 ? '' : 's'} needed`;
  const overflow = canNameFirst && items.length > 1 ? items.length - 1 : 0;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={event => {
          setAnchorEl(event.currentTarget);
          onOpen?.();
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: '999px',
          border: `1px solid ${accent}`,
          backgroundColor: accentTint,
          cursor: 'pointer',
          font: 'inherit',
          '&:hover': { backgroundColor: accentSoft },
        }}
      >
        <OwedDot filled={settled} color={accent} />
        <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: accent }}>
          {primaryText}
        </Typography>
        {overflow > 0 && (
          <Typography component="span" sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft }}>
            +{overflow} item{overflow === 1 ? '' : 's'}
          </Typography>
        )}
      </Box>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: isManagement ? 560 : 340 } } }}
      >
        <Stack spacing={1.25}>
          {/*
            Recorder only. On the management surface the manager renders its
            own header ("N items still needed", with Delivery history beside
            it), and this sat directly above it saying the same thing twice.
            The error and settled wordings go with it — the manager's own error
            alert and rows already say those.
          */}
          {!isManagement && (
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: ELIG_TOKENS.ink }}>
              {errored ? (
                `We could not load this ${subject.singular}'s requirements`
              ) : settled ? (
                `Nothing outstanding for this ${subject.singular}`
              ) : (
                <>
                  {items.length} {subject.singular} item{items.length === 1 ? '' : 's'} still
                  missing
                </>
              )}
            </Typography>
          )}

          {/*
            The old second line here read "…most finish the secure link in
            about two minutes". It carried a duration and an unquantified
            "most" with nothing behind either, so it has gone rather than been
            reworded (PM, GDN-3592). What is left is the part that is true and
            useful: this is worth raising while they are here.
          */}
          {!isManagement && (
            <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft, lineHeight: 1.4 }}>
              Worth a mention while the {subject.singular} is in the room.
            </Typography>
          )}

          {/*
            The management popover carries the whole surface the record section
            used to (GDN-3592): a row per requirement with its state and detail
            line, per-row Send / Remind / Waive, the waived group, and delivery
            history. It is the record section's own view, not a second
            implementation of it — which is why every settled ruling it already
            obeys (GDN-3549's step-less Send, GDN-3558's 409 message,
            GDN-3510's waive actor and reason, AC-4's Remind) holds here for
            free rather than by being re-derived.
          */}
          {isManagement ? (
            managementContent
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {items.map(item => (
                <Box
                  component="li"
                  key={item.patientRequirementId}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
                >
                  <OwedDot />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: ELIG_TOKENS.ink }}>
                    {item.label ?? `An item your practice needs`}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {!isManagement && remindOutcome?.kind === 'sent' && (
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: ELIG_TOKENS.green }}>
              Sent. The badge clears once they finish.
            </Typography>
          )}
          {!isManagement && remindOutcome?.kind === 'failed' && (
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: ELIG_TOKENS.red }}>
              We could not send that. Try from the {subject.singular} record.
            </Typography>
          )}
          {/*
            Amber, and the server's words alone.

            Green would read as "done", which is wrong for the case where
            something is still outstanding and merely has no patient step yet.
            Red is the failure above, and it ends in advice — try from the
            record — that is wrong here twice over: a 409 is the server's
            settled answer, so retrying anywhere cannot change it. Amber says
            "read this", commits to neither, and prompts no retry.
          */}
          {!isManagement && remindOutcome?.kind === 'declined' && (
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: ELIG_TOKENS.amber }}>
              {remindOutcome.message}
            </Typography>
          )}

          {/*
            The glance variant's one action is a send for the whole patient,
            because a provider mid-visit is the wrong person to be choosing
            between items. The management popover has a per-row Send / Remind
            instead, so a second whole-patient button here would be a way to
            message someone about items you had not looked at — and "Open
            record" is meaningless on the record.
          */}
          {!isManagement && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {onRemind && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={onRemind}
                  disabled={isReminding}
                  startIcon={
                    isReminding ? <CircularProgress size={12} color="inherit" /> : undefined
                  }
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Remind {subject.singular}
                </Button>
              )}
              {onOpenPatient && (
                <Button
                  size="small"
                  variant="text"
                  onClick={onOpenPatient}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Open record
                </Button>
              )}
            </Stack>
          )}

          {/*
            Glance only. On the management variant this sat directly under a
            Waive button and contradicted it — "it cannot be dismissed while
            items stay open" is true of the recorder's badge, which offers no
            way to act, and false on a surface whose whole purpose is acting on
            the items. No replacement copy: the manager's own rows already say
            what each requirement's state is.
          */}
          {!isManagement && (
            <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint, lineHeight: 1.4 }}>
              This clears on its own when evidence arrives. It cannot be dismissed while items stay
              open.
            </Typography>
          )}
        </Stack>
      </Popover>
    </>
  );
};
